import React, { useMemo } from "react";
import { CheckSquare2, AlertTriangle, Clock, CheckCircle2, Square } from "lucide-react";

const LORA  = { fontFamily: "'Lora', Georgia, serif" };
const SERIF = { fontFamily: "'Cormorant Garamond', Georgia, serif" };

const URGENCE_ORDER = { en_retard: 0, urgent: 1, a_venir: 2, completee: 3 };

export const CATEGORY_CONFIG = {
  documents:  { emoji: "📄", label: "Documents" },
  sante:      { emoji: "🏥", label: "Santé" },
  finances:   { emoji: "💳", label: "Finances" },
  logistique: { emoji: "🗺️", label: "Logistique" },
  bagages:    { emoji: "🧳", label: "Bagages" },
  numerique:  { emoji: "📱", label: "Numérique" },
  securite:   { emoji: "🔒", label: "Sécurité" },
};

// ── Urgence badge ─────────────────────────────────────────────────────────────
function UrgenceBadge({ task }) {
  if (task.completed) {
    return (
      <span className="flex items-center gap-1 text-xs text-emerald-600 font-medium" style={LORA}>
        <CheckCircle2 size={12} strokeWidth={2} /> Fait
      </span>
    );
  }
  if (task.urgence === "en_retard") {
    return (
      <span className="text-xs bg-red-100 text-red-700 border border-red-200 px-2 py-0.5 rounded-sm whitespace-nowrap" style={LORA}>
        En retard
      </span>
    );
  }
  if (task.urgence === "urgent") {
    const d = task.days_until;
    return (
      <span className="text-xs bg-amber-100 text-amber-700 border border-amber-200 px-2 py-0.5 rounded-sm whitespace-nowrap" style={LORA}>
        {d === 0 ? "Aujourd'hui" : d === 1 ? "Demain" : `Dans ${d} j`}
      </span>
    );
  }
  if (task.date_limite) {
    const d = new Date(task.date_limite);
    const label = d.toLocaleDateString("fr-FR", { day: "numeric", month: "short" });
    return (
      <span className="text-xs text-[#9A8F86] border border-[#DCD4C6] px-2 py-0.5 rounded-sm whitespace-nowrap" style={LORA}>
        {label}
      </span>
    );
  }
  return null;
}

// ── Task card ─────────────────────────────────────────────────────────────────
export function TaskCard({ task, onToggle, isLoading }) {
  const isCompleted = task.completed;

  return (
    <div
      className={`flex items-start gap-3 p-3 rounded-sm border transition-all ${
        isCompleted
          ? "border-[#DCD4C6] bg-[#F9F6F0] opacity-60"
          : task.urgence === "en_retard"
          ? "border-red-200 bg-red-50/40 hover:border-red-300"
          : task.urgence === "urgent"
          ? "border-amber-200 bg-amber-50/40 hover:border-amber-300"
          : "border-[#DCD4C6] bg-white hover:border-[#C17F59]/30"
      }`}
      data-testid={`task-card-${task.id}`}
    >
      {/* Checkbox */}
      <button
        onClick={() => !isLoading && onToggle(task.id, !isCompleted)}
        disabled={isLoading}
        className="flex-shrink-0 mt-0.5 text-[#9A8F86] hover:text-[#C17F59] transition-colors disabled:opacity-50"
        data-testid={`task-toggle-${task.id}`}
        aria-label={isCompleted ? "Marquer comme non fait" : "Marquer comme fait"}
      >
        {isLoading ? (
          <div className="w-4 h-4 border-2 border-[#C17F59] border-t-transparent rounded-full animate-spin" />
        ) : isCompleted ? (
          <CheckSquare2 size={18} strokeWidth={1.5} className="text-[#C17F59]" />
        ) : (
          <Square size={18} strokeWidth={1.5} />
        )}
      </button>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-start gap-2 flex-wrap">
          <span
            className={`text-sm leading-snug ${isCompleted ? "line-through text-[#9A8F86]" : "text-[#2B2623]"}`}
            style={LORA}
          >
            {task.titre}
          </span>
          {task.bloquante && !isCompleted && (
            <AlertTriangle size={13} strokeWidth={1.5} className="text-red-400 flex-shrink-0 mt-0.5" title="Tâche bloquante" />
          )}
        </div>
        {task.description && (
          <p className="text-xs text-[#9A8F86] mt-0.5 leading-relaxed" style={LORA}>
            {task.description}
          </p>
        )}
      </div>

      {/* Badge */}
      <div className="flex-shrink-0">
        <UrgenceBadge task={task} />
      </div>
    </div>
  );
}

// ── Task group ────────────────────────────────────────────────────────────────
export function TaskGroup({ category, tasks, onToggle, loadingIds }) {
  const config = CATEGORY_CONFIG[category] || { emoji: "•", label: category };
  const sorted = useMemo(
    () => [...tasks].sort((a, b) => (URGENCE_ORDER[a.urgence] ?? 4) - (URGENCE_ORDER[b.urgence] ?? 4)),
    [tasks]
  );

  if (sorted.length === 0) return null;

  return (
    <div className="mb-6" data-testid={`task-group-${category}`}>
      <h3
        className="text-sm uppercase tracking-widest text-[#8B5E34] mb-2 flex items-center gap-1.5"
        style={LORA}
      >
        <span>{config.emoji}</span> {config.label}
      </h3>
      <div className="space-y-2">
        {sorted.map((task) => (
          <TaskCard
            key={task.id}
            task={task}
            onToggle={onToggle}
            isLoading={loadingIds.has(task.id)}
          />
        ))}
      </div>
    </div>
  );
}
